import { Plus, Trash2, Type, AlignLeft, Hash, Mail, ListChecks, List, Dot, CheckSquare, X } from "lucide-react";
import { toast } from "react-toastify";
import { dynamicFormDelete } from "../../../../services/dynamicFormService";
import ConfirmationPopup from "../../../common/ConfirmationPopup";
import { useState } from "react";

const fieldTypes = [
  { value: "input", label: "Short Answer", icon: <Type className="w-4 h-4" /> },
  { value: "textarea", label: "Paragraph", icon: <AlignLeft className="w-4 h-4" /> },
  { value: "number", label: "Number", icon: <Hash className="w-4 h-4" /> },
  { value: "email", label: "Email", icon: <Mail className="w-4 h-4" /> },
  { value: "select", label: "Dropdown", icon: <List className="w-4 h-4" /> },
  { value: "multiselect", label: "Multi Select", icon: <ListChecks className="w-4 h-4" /> },
  { value: "radio", label: "Multiple Choice", icon: <Dot className="w-4 h-4" /> },
  { value: "checkbox", label: "Checkboxes", icon: <CheckSquare className="w-4 h-4" /> },
];

export default function FormFields({ index, field, onAdd, onUpdate, onDelete }) {

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // const handleDelete = async () => {
  //   try {
  //     if (field._id) {
  //       await dynamicFormDelete({ids:[field._id],contentType:"field"});
  //       toast.success("Field deleted successfully");
  //     }
  //     onDelete(field._id || field.tempId);
  //   } catch (err) {
  //     console.error("Delete error:", err);
  //     toast.error("Failed to delete field");
  //   }
  // };

  const handleDelete = async () => {
    try {
      setLoading(true);
      if (field._id) {
        await dynamicFormDelete(field._id);
        toast.success("Field deleted successfully");
      }
      onDelete(field._id || field.tempId);
      setIsConfirmOpen(false);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete field");
    } finally {
      setLoading(false);
    }
  };

  const addOption = () => {
    const newOptions = [...field.options, { label: { text: `Option ${field.options.length + 1}` }, default: false }];
    onUpdate(field._id || field.tempId, { ...field, options: newOptions });
  };

  const updateOption = (index, value) => {
    const newOptions = field.options.map((opt, i) => (i === index ? { ...opt, label: { text: value } } : opt));
    onUpdate(field._id || field.tempId, { ...field, options: newOptions });
  };

  const deleteOption = (index) => {
    const newOptions = field.options.filter((_, i) => i !== index);
    onUpdate(field._id || field.tempId, { ...field, options: newOptions });
  };

  return (
    <div className="group relative bg-white rounded-xl shadow-sm border-l-4 border-l-primary p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <input
          type="text"
          value={field.label?.text || ""}
          onChange={(e) =>
            onUpdate(field._id || field.tempId, {
              ...field,
              label: { ...field.label, text: e.target.value },
            })
          }
          placeholder="Question"
          className="flex-1 text-lg font-medium text-gray-800 outline-none border-b border-transparent focus:border-primary"
        />

        <div className="relative flex items-center">
          <span className="absolute left-2 pointer-events-none text-gray-500">{fieldTypes.find((f) => f.value === field.type)?.icon}</span>
          <select
            value={field.type}
            onChange={(e) =>
              onUpdate(field._id || field.tempId, {
                ...field,
                type: e.target.value,
                options: [],
              })
            }
            className="appearance-none border rounded-md pl-8 pr-8 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-primary focus:outline-none"
          >
            {fieldTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <button onClick={() => setIsConfirmOpen(true)} className="ml-2 text-red-500 hover:text-red-700 transition">
          <Trash2 className="w-5 h-5 mr-8" />
        </button>
      </div>
      <span
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 
             bg-white px-1.5 py-0.5 text-xs font-medium text-gray-700 rounded-md border border-primary
             origin-center -rotate-90"
      >
        {`Field ${index + 1}`}
      </span>

      {field.type === "input" && (
        <input
          type="text"
          placeholder="Short answer"
          className="w-1/2 border-b border-gray-300 text-sm text-gray-500 bg-transparent outline-none"
          disabled
        />
      )}
      {field.type === "textarea" && (
        <textarea placeholder="Long answer" className="w-1/2 border-b border-gray-300 text-sm text-gray-500 bg-transparent outline-none" disabled />
      )}
      {field.type === "number" && (
        <input
          type="number"
          placeholder="Number"
          className="w-1/2 border-b border-gray-300 text-sm text-gray-500 bg-transparent outline-none"
          disabled
        />
      )}
      {field.type === "email" && (
        <input
          type="email"
          placeholder="Email"
          className="w-1/2 border-b border-gray-300 text-sm text-gray-500 bg-transparent outline-none"
          disabled
        />
      )}

      {(field.type === "radio" || field.type === "checkbox" || field.type === "select" || field.type === "multiselect") && (
        <div className="space-y-2">
          {field.options.map((opt, index) => (
            <div key={index} className="flex items-center gap-2">
              {(field.type === "radio" || field.type === "checkbox") && <input type={field.type} disabled className="text-primary" />}
              <input
                type="text"
                value={opt.label?.text}
                onChange={(e) => updateOption(index, e.target.value)}
                className="w-3/5 border-b border-gray-300 text-sm text-gray-700 outline-none focus:border-primary"
              />
              <button type="button" onClick={() => deleteOption(index)} className="text-red-500 hover:text-red-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button type="button" onClick={addOption} className="flex items-center gap-1 text-primary text-sm hover:underline">
            <Plus className="w-4 h-4" /> Add option
          </button>
        </div>
      )}

      {onAdd && (
        <div className="">
          <button
            type="button"
            onClick={onAdd}
            className="absolute -right-5 top-1/2 -translate-y-1/2 
               bg-green-600 text-white p-3 rounded-full shadow-lg 
               hover:bg-green-500/90 transition
               opacity-0 group-hover:opacity-100
              group/btn"
          >
            <Plus className="w-5 h-5" />
            <span
              className="absolute left-full ml-2 top-1/2 -translate-y-1/2 
                 bg-green-500 text-white text-xs px-2 py-1 rounded-md 
                 opacity-0 group-hover/btn:opacity-100 
                 transition pointer-events-none whitespace-nowrap"
            >
              Add Field
            </span>
          </button>
        </div>
      )}

      <ConfirmationPopup
        isOpen={isConfirmOpen}
        setIsOpen={setIsConfirmOpen}
        getActionValue={(action) => {
          if (action.button_type === "confirm") {
            handleDelete();
          }
        }}
        title="Delete Field"
        message="Are you sure you want to delete this field? This action cannot be undone."
        confirmButtonLabel="Yes, Delete"
        cancelButtonLabel="Cancel"
        setLoading={setLoading}
        loading={loading}
      />
    </div>
  );
}
